/**
 * Blog Post: How to Use SDAB Expert
 * Author: Technical Expert (Alex Chen)
 */

import type { BlogPost } from "../types";

export const howToUseSdabPost: BlogPost = {
  slug: "how-to-use-sdab-expert",
  title: "SDAB Expert: Predicting Development Appeal Success in Calgary",
  description:
    "Learn how to use Builder Buddy's SDAB Expert system to assess risk for setback relaxations, variance requests, and development permit appeals in Calgary.",
  excerpt:
    "Planning a project that needs a variance or setback relaxation? Our SDAB Expert helps you understand your chances of approval.",
  author: "technical-expert",
  category: "tools",
  featuredImage: "/blog/sdab-hero.jpg",
  tags: [
    "SDAB",
    "development permit",
    "variance",
    "setback",
    "Calgary zoning",
    "appeals",
  ],
  publishedAt: "2026-01-26",
  readingTime: 6,
  tableOfContents: [
    { id: "what-is-sdab", title: "What is the SDAB?", level: 2 },
    { id: "when-you-need-it", title: "When You Need an Appeal", level: 2 },
    { id: "using-the-tool", title: "Using the SDAB Expert Tool", level: 2 },
    {
      id: "understanding-risk",
      title: "Understanding Risk Assessment",
      level: 2,
    },
  ],
  callToAction: {
    title: "Assess your project",
    description: "Check the appeal risk for your development.",
    buttonText: "Open SDAB Expert",
    buttonLink: "/sdab",
  },
  content: `
Infill development in Calgary often requires variances from standard zoning rules. Understanding your project's approval likelihood before investing significant resources can save time and money. That's where our SDAB Expert tool comes in.

## What is the SDAB?

The **Subdivision and Development Appeal Board (SDAB)** is a quasi-judicial body that hears appeals of:
- Development permit decisions
- Subdivision decisions
- Certain compliance orders

When you apply for a development permit with relaxations (variances from the Land Use Bylaw), Calgary's planning department may approve it directly. However, affected neighbours can appeal approvals, or you can appeal if your application is refused.

The SDAB considers factors like:
- Impact on neighbouring properties
- Previous decisions on similar requests
- The specific circumstances of your site
- Whether the variance is reasonable given constraints

## When You Need an Appeal

You might need SDAB involvement if your project requires:

**Setback Relaxations**
- Building closer to the front, rear, or side property line than normally allowed
- Common for infill projects on smaller lots

**Height Variances**
- Building taller than the zone allows
- Often needed for infill that matches existing streetscape

**Lot Coverage Exceedance**
- Covering more of the lot with buildings than permitted
- Frequent in R-CG and other infill zones

**Parking Reductions**
- Fewer parking spaces than required
- Common for projects near transit

**Use Variances**
- Proposing a use not listed as permitted in the zone
- Less common and harder to obtain

## Using the SDAB Expert Tool

**Step 1: Enter Your Address**

Start by entering the property address. Our tool will automatically populate:
- Current zoning designation
- Community name
- Relevant setback requirements

**Step 2: Describe Your Project**

Enter details about what you're proposing:
- Building area and lot area
- Proposed height
- Proposed setbacks (front, rear, sides)
- Number of parking spaces

**Step 3: Review Risk Assessment**

Our expert system analyzes your project against:
- Current bylaw requirements
- Historical SDAB decisions in your community
- Similar cases across Calgary

You'll receive:
- Overall risk rating (Low, Medium, High)
- Specific factors affecting your case
- Similar past cases with outcomes

## Understanding Risk Assessment

**Low Risk (70%+ approval likelihood)**

Your project:
- Requires minor relaxations
- Has precedent of approval for similar requests
- Minimal impact on neighbours
- Addresses practical site constraints

**Medium Risk (40-70% approval likelihood)**

Your project:
- Requires moderate relaxations
- Mixed precedent for similar requests
- Some potential neighbour concerns
- May need design modifications to improve chances

**High Risk (<40% approval likelihood)**

Your project:
- Requires significant relaxations
- Limited or negative precedent
- Likely neighbour opposition
- May need substantial redesign

### Key Factors We Analyze

1. **Magnitude of relaxation** - A 5% setback reduction is easier to approve than 30%
2. **Precedent in community** - Has this type of relaxation been approved nearby?
3. **Site constraints** - Is there a valid reason (odd lot shape, existing structures) for the request?
4. **Impact on neighbours** - Will the variance affect sunlight, privacy, or views?
5. **Neighbourhood context** - Does the proposal fit the existing character?

**Similar Cases**

We display historical SDAB decisions for similar projects. This helps you understand:
- What arguments worked in approved cases
- What concerns led to refusals
- How your specific community has responded to similar requests

**Important Considerations**

The SDAB Expert provides analysis based on historical data and bylaw requirements. However, every case is unique. Factors that can affect outcomes include:

- Specific neighbour concerns (or support)
- Quality of your application materials
- Current political climate around development
- Changes to policy since historical decisions

We recommend using this tool for preliminary assessment, but consulting with a planning professional for significant projects or high-risk scenarios.
`,
};
